import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Subject, takeUntil } from "rxjs";
import { AppService } from "src/app/_services/app.service";

interface ReviewItem {
  name: string;
  locationKey?: string;
  location?: string;
  textKey?: string;
  text?: string;
  avatar: string;
  stars: number;
  averageRating?: number;
  createdAt?: string;
}

@Component({
  selector: "app-reviews-section",
  templateUrl: "./reviews-section.component.html",
  styleUrls: ["./reviews-section.component.scss"],
})
export class ReviewsSectionComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  readonly skeletonCards = [1, 2, 3];
  isLoading = true;
  reviews: ReviewItem[] = [];
  averageScore = 0;
  reviewsCount = 0;

  constructor(private appService: AppService, private router: Router) {}

  ngOnInit(): void {
    this.loadTopEvolutions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get averageScoreLabel(): string {
    return `${this.averageScore.toFixed(1)}/5`;
  }

  get roundedAverageStars(): number {
    return this.toStars(this.averageScore);
  }

  get reviewsCountLabel(): string {
    return this.reviewsCount.toLocaleString("fr-FR");
  }

  getReviewText(review: ReviewItem): string {
    return review.text || "";
  }

  getReviewLocation(review: ReviewItem): string {
    return review.location || "";
  }

  openAllReviews() {
    (document.activeElement as HTMLElement | null)?.blur();
    this.router.navigate(["/particulier/avis"]);
  }

  private loadTopEvolutions() {
    this.appService
      .getTopEvolutions({ limit: 10, finished_demande: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const items = this.mapEvolutions(response);
          this.isLoading = false;
          if (!items.length) {
            this.reviews = [];
            this.averageScore = 0;
            this.reviewsCount = 0;
            return;
          }

          this.reviews = items;
          const summaryAvg = this.toNumber(response?.data?.summary?.average_rating);
          const summaryTotal = this.toNumber(response?.data?.summary?.total_count);
          if (summaryAvg > 0) {
            this.averageScore = summaryAvg;
          } else {
            const sum = items.reduce((acc, item) => acc + (item.averageRating || item.stars), 0);
            this.averageScore = sum / items.length;
          }
          this.reviewsCount = summaryTotal > 0 ? summaryTotal : items.length;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  private mapEvolutions(response: any): ReviewItem[] {
    const rawItems = Array.isArray(response?.data?.items)
      ? response.data.items
      : Array.isArray(response?.items)
        ? response.items
        : [];
    if (!rawItems.length) return [];

    return [...rawItems]
      .sort((a: any, b: any) => {
        const ratingDelta =
          this.toNumber(b?.rating ?? b?.average_rating) - this.toNumber(a?.rating ?? a?.average_rating);
        if (ratingDelta !== 0) return ratingDelta;
        return this.getTimestampMs(b?.created_at) - this.getTimestampMs(a?.created_at);
      })
      .map((item: any) => {
        const averageRating = this.toNumber(item?.rating ?? item?.average_rating);
        const stars = this.toStars(averageRating);
        const name = (
          item?.client_display_name ||
          item?.client_full_name ||
          item?.prestataire_display_name ||
          "BOB"
        ).toString();
        const domain = (item?.domaine_label || "").toString().trim();
        const reference = (item?.demande_reference || "").toString().trim();
        const comment = (item?.comment || "").toString().trim();

        const fallbackText = comment || [domain, reference].filter(Boolean).join(" - ") || `${averageRating}/5`;

        const location = [domain, reference].filter(Boolean).join(" - ");
        const avatarUrl = this.pickAvatarUrl(item?.client_avatar_url);
        return {
          name,
          location,
          text: fallbackText,
          avatar:
            avatarUrl ??
            `https://i.pravatar.cc/100?u=${encodeURIComponent(
              (item?.client_full_name || name).toString()
            )}`,
          stars,
          averageRating,
          createdAt: item?.created_at,
        } as ReviewItem;
      });
  }

  private pickAvatarUrl(value: any): string | undefined {
    const url = String(value || "").trim();
    return url ? url : undefined;
  }

  private getTimestampMs(value: any): number {
    const time = new Date(value || "").getTime();
    return Number.isNaN(time) ? 0 : time;
  }

  private toNumber(value: any): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private toStars(rating: number): number {
    const rounded = Math.round(rating);
    return Math.min(5, Math.max(0, rounded));
  }
}
